import { useEffect, useState } from 'react';
import { ArrowUpRight, Gift, Sparkles } from 'lucide-react';
import kuickoMark from '@/assets/images/kuicko/kuicko-mark.png';
import { getEventPartnerOffer } from '@/api/partnerRewards';
import { track } from '@/lib/analytics';

const KUICKO_URL = 'https://www.kuicko.in/';
const endFmt = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' });

/** One fetch per event per page view, shared by every card on the page. */
const cache = new Map();
const loadOffer = (serviceId) => {
  if (!cache.has(serviceId)) cache.set(serviceId, getEventPartnerOffer(serviceId).catch(() => ({ offer: null })));
  return cache.get(serviceId);
};

/**
 * "Kuicko Promise" — brand strip for LocalKonnect's sister app on event pages.
 * When a live partner campaign covers this event it promotes the reward the
 * booking earns (sent to the customer's Kuicko account automatically);
 * otherwise it is a quiet "in partnership with Kuicko" strip. `compact` is the
 * one-line version for the ticket picker.
 */
const KuickoPromise = ({ serviceId, compact = false, className = '' }) => {
  const [offer, setOffer] = useState(undefined);

  useEffect(() => {
    let live = true;
    if (serviceId) loadOffer(serviceId).then((d) => live && setOffer(d?.offer ?? null));
    return () => {
      live = false;
    };
  }, [serviceId]);

  if (offer === undefined) return null;

  const onOpen = () => track('kuicko_promo_click', { serviceId, hasOffer: !!offer, compact });
  const min = offer?.minBookingAmount >= 50 ? `₹${Math.round(offer.minBookingAmount)}+` : null;
  const ends = offer?.endsAt ? endFmt.format(new Date(offer.endsAt)) : null;

  if (compact) {
    return (
      <div className={`flex items-center gap-3 rounded-xl border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 px-3 py-2.5 ${className}`}>
        <img src={kuickoMark} alt="Kuicko" className="h-8 w-8 shrink-0 rounded-lg" />
        <p className="min-w-0 flex-1 text-xs text-ink-700">
          {offer ? (
            <>
              <span className="font-bold text-ink-900">Kuicko Promise:</span> book{min ? ` ${min}` : ''} and get{' '}
              <span className="font-semibold text-orange-700">{offer.summary}</span> on Kuicko — added automatically.
            </>
          ) : (
            <>
              <span className="font-bold text-ink-900">In partnership with Kuicko</span> — food &amp; essentials, delivered fast.
            </>
          )}
        </p>
        <Gift className="h-4 w-4 shrink-0 text-orange-500" aria-hidden="true" />
      </div>
    );
  }

  return (
    <section
      className={`relative overflow-hidden rounded-2xl border border-orange-200 bg-gradient-to-br from-orange-50 via-amber-50 to-white p-5 ${className}`}
      aria-labelledby="kuicko-promise-title"
    >
      <Sparkles className="absolute -right-2 -top-2 h-16 w-16 text-orange-200/70" aria-hidden="true" />
      <div className="relative flex items-start gap-3">
        <img src={kuickoMark} alt="Kuicko" className="h-12 w-12 shrink-0 rounded-xl shadow-card" />
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wide text-orange-600">
            {offer ? 'Kuicko Promise' : 'In partnership with'}
          </p>
          <h3 id="kuicko-promise-title" className="font-display text-base font-bold text-ink-900">
            {offer ? `Book${min ? ` ${min}` : ''} & unlock a Kuicko reward` : 'Kuicko — food & essentials, delivered'}
          </h3>
          <p className="mt-1 text-sm text-ink-600">
            {offer ? (
              <>
                Get <span className="font-semibold text-orange-700">{offer.summary}</span> on your next Kuicko order. It
                lands in your Kuicko account automatically after you book.
              </>
            ) : (
              'Grab a bite before the show or get essentials delivered — Kuicko is LocalKonnect’s delivery partner in your city.'
            )}
          </p>
          {offer && (
            <p className="mt-1 text-[11px] text-ink-500">
              {ends ? `Offer till ${ends}` : 'Limited period'}
              {offer.perMonth ? ` · ${offer.perMonth} reward per number per month` : ''} · same mobile number on Kuicko
            </p>
          )}
          <a
            href={KUICKO_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onOpen}
            className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-orange-700 hover:underline"
          >
            Explore Kuicko <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default KuickoPromise;
